import React, { useEffect, useState } from 'react';
import _ from 'underscore';
import ReactTooltip from 'react-tooltip';
import Button from '../../components/button';
import Checkbox from '../../components/checkbox';
import Input from '../../components/input';
import Error from '../../components/error_message';
import { useAngularService } from 'helpers/get_angular_service';
import notification from 'helpers/notification';
import api from 'helpers/api';
import useOnMount from 'helpers/use_on_mount';
import Link from '../../components/link';
import Select from '../../components/select';
import FailureFeeForm from '../../components/failure_fee_form';
import useGym from 'helpers/use_gym';
import { useExtendState } from 'helpers/use_extend_state';
import { GymMembershipDetail } from '../../types/gym';
import Label from '../../components/label';
import Tooltip from 'react/components/tooltip';
import CancellationPolicyForm, {
  CancellationPolicyFormEnum,
} from '../../pages/account_profile/cancellation_policy_form';

const GymProfileMembershipPayments = () => {
  const [errors, setErrors] = useState<any>({});
  const [showAdvancedPaymentSettings, setShowAdvancedPaymentSettings] = useState(false);

  const loadingAnimation = useAngularService('loadingAnimation');

  const $rootScope = useAngularService('$rootScope');
  const $stateParams = useAngularService('$stateParams');
  const GTMService = useAngularService('GTMService');
  const gym = useGym();
  const [gymMembershipDetail, extendGymMembershipDetail] = useExtendState<GymMembershipDetail>(
    () => ({ ...gym.gym_membership_detail }),
  );

  useOnMount(() => {
    GTMService.trackPage('Account Profile - Membership Payments');
  });

  const save = () => {
    GTMService.trackPage('Account Profile - Membership Payments', 'attempt');
    const message =
      'Are you sure? This will update the failure fee settings for all active memberships and membership plans.';
    if (
      gymMembershipDetail.apply_failure_fee_changes_to_all_memberships &&
      !window.confirm(message)
    )
      return;

    loadingAnimation.show();
    const url = `gyms/${$stateParams.gymId}/update_membership_detail`;

    setErrors({});
    api.put(url, { gym_membership_detail_attributes: gymMembershipDetail }).then(
      (response) => {
        GTMService.trackPage('Account Profile - Membership Payments', 'success');
        loadingAnimation.hide();
        $rootScope.gym.gym_membership_detail = response;
        notification.success('Saved successfully');
      },
      (response) => {
        GTMService.trackPage('Account Profile - Membership Payments', 'error');
        setErrors(response.data.errors);
        notification.error('Save failed. Please fix the errors below');
        loadingAnimation.hide();
      },
    );
  };

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [showAdvancedPaymentSettings]);

  return (
    <div>
      <Tooltip type="light" v2 />
      <div className="content-box-mini">
        <h4 className="light first-one">Membership Payments</h4>
        <label>Default tax rate for Membership Payments</label>
        &nbsp;&nbsp;
        <span className="helper-text first-one last-one"></span>
        <Link route="gym.pos.taxRates.list">Manage Tax Rates</Link>
        <div className="row">
          <div className="col-md-8">
            <div className="form-group">
              <div className="margin-bottom-16">
                <Select
                  value={gymMembershipDetail.membership_tax_rate_id}
                  options={gymMembershipDetail.membership_tax_rate_select_options}
                  getLabel={(item) => item[0]}
                  getValue={(item) => item[1]}
                  onChange={(membership_tax_rate_id) =>
                    extendGymMembershipDetail({ membership_tax_rate_id })
                  }
                  allowEmptyOption
                />
              </div>

              <Checkbox
                checked={gymMembershipDetail.membership_tax_exempt}
                onChange={(membership_tax_exempt) =>
                  extendGymMembershipDetail({ membership_tax_exempt })
                }
              >
                Or, treat membership payments as tax-exempt
              </Checkbox>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-8">
            <Label>Payment Receipt title</Label>
            <i
              className="hint icon-information"
              data-tip="This will appear at the top of the receipts that are emailed to contacts whenever
              their payments are marked as paid"
            />
            <div className="form-group">
              <Input
                value={gymMembershipDetail.invoice_receipt_header}
                onChange={(invoice_receipt_header) =>
                  extendGymMembershipDetail({ invoice_receipt_header })
                }
              />
              <Error error={errors?.invoice_receipt_header} />
            </div>
            <div className="form-group">
              <Checkbox
                checked={gymMembershipDetail.show_member_number_on_receipts}
                onChange={(show_member_number_on_receipts) =>
                  extendGymMembershipDetail({ show_member_number_on_receipts })
                }
              >
                Show member number on receipts? (Members only)
              </Checkbox>
            </div>
          </div>
        </div>
      </div>
      {gym.has_payment_provider ? (
        <div className="margin-bottom-44">
          <div className="margin-bottom-16">
            <FailureFeeForm
              data={gymMembershipDetail}
              errors={errors}
              updateData={extendGymMembershipDetail}
            />
          </div>

          <div>
            <div className="margin-bottom-16">
              <Checkbox
                checked={gymMembershipDetail.apply_failure_fee_changes_to_all_memberships}
                falseValue={0}
                trueValue={1}
                onChange={(apply_failure_fee_changes_to_all_memberships) =>
                  extendGymMembershipDetail({ apply_failure_fee_changes_to_all_memberships })
                }
              >
                Apply failure fee changes to all active memberships and membership plans
              </Checkbox>
            </div>
          </div>
        </div>
      ) : null}

      <CancellationPolicyForm
        cancellationPolicyConfig={gymMembershipDetail}
        extendCancellationPolicyConfig={extendGymMembershipDetail}
        type={CancellationPolicyFormEnum.MEMBERSHIP}
        errors={errors}
      />

      <Button
        variant="link"
        onClick={() => setShowAdvancedPaymentSettings(!showAdvancedPaymentSettings)}
      >
        {showAdvancedPaymentSettings ? 'Hide' : 'Show'} advanced payment settings{' '}
        <i
          className={`margin-top-4 ${
            showAdvancedPaymentSettings ? 'icon-chevron-up' : 'icon-chevron-down'
          }`}
        />
      </Button>
      {showAdvancedPaymentSettings && (
        <div className="content-box-mini">
          <h4 className="light first-one">Advanced payment settings</h4>

          <div className="row">
            <div className="col-md-8">
              <div className="form-group">
                <Label>Membership class allowances are used up by:</Label>
                <Select
                  value={gymMembershipDetail.track_general_attendance_against_memberships.toString()}
                  options={[
                    {
                      label: 'Bookings only',
                      value: 'false',
                    },
                    {
                      label: 'Bookings and General Attendances',
                      value: 'true',
                    },
                  ]}
                  onChange={(track_general_attendance_against_memberships) =>
                    extendGymMembershipDetail({
                      track_general_attendance_against_memberships:
                        track_general_attendance_against_memberships === 'true',
                    })
                  }
                />

                {gymMembershipDetail.track_general_attendance_against_memberships && (
                  <div className="danger-color">
                    This setting means that general check-ins will require an active membership.
                  </div>
                )}
              </div>
              <div className="form-group">
                <Label>Recurring membership class allowances are renewed based on:</Label>
                <Select
                  value={gymMembershipDetail.membership_limit_type}
                  options={[
                    {
                      label: 'Calendar period (end of week, month or year)',
                      value: 'calendar_membership_limits',
                    },
                    {
                      label: 'Membership anniversary (days since membership started)',
                      value: 'anniversary_membership_limits',
                    },
                  ]}
                  onChange={(membership_limit_type) =>
                    extendGymMembershipDetail({
                      membership_limit_type,
                    })
                  }
                />
              </div>
              <div className="form-group">
                <Label>Expire class pack memberships:</Label>
                <Select
                  value={gymMembershipDetail.class_pack_expiration_policy}
                  options={[
                    {
                      label: 'When membership expiration date has been reached',
                      value: 'expire_class_packs_on_membership_expiry',
                    },
                    {
                      label: 'When all classes have been booked (or expiry date reached)',
                      value: 'expire_class_packs_on_booking_creation',
                    },
                    {
                      label:
                        'When all classes have been booked AND attended (or expiry date reached)',
                      value: 'expire_class_packs_on_booking_attendance',
                    },
                  ]}
                  onChange={(class_pack_expiration_policy) =>
                    extendGymMembershipDetail({ class_pack_expiration_policy })
                  }
                />
              </div>
              <div className="form-group">
                <Label>Allow unused, unexpired make-up classes to be used:</Label>
                <Select
                  value={gymMembershipDetail.expired_membership_make_up_class_expiry_policy}
                  options={[
                    {
                      label: 'Forever',
                      value: 'no_limit_for_make_up_classes_for_expired_memberships',
                    },
                    {
                      label: 'Up to 7 days after membership expires',
                      value: 'allow_make_up_classes_for_expired_memberships_up_to_7d',
                    },
                    {
                      label: 'Up to 30 days after membership expires',
                      value: 'allow_make_up_classes_for_expired_memberships_up_to_30d',
                    },
                    {
                      label: 'Up to 90 days after membership expires',
                      value: 'allow_make_up_classes_for_expired_memberships_up_to_90d',
                    },
                    {
                      label: 'Up to 6 months after membership expires',
                      value: 'allow_make_up_classes_for_expired_memberships_up_to_6m',
                    },
                    {
                      label: 'Up to 1 year after membership expires',
                      value: 'allow_make_up_classes_for_expired_memberships_up_to_1y',
                    },
                  ]}
                  onChange={(expired_membership_make_up_class_expiry_policy) =>
                    extendGymMembershipDetail({ expired_membership_make_up_class_expiry_policy })
                  }
                />
                <div className="helper-text">
                  If set, this limit will apply to <strong>the day when a booking is made</strong>,
                  rather than the day of the event.
                </div>
              </div>
              <div className="form-group">
                <Label>
                  {gym.has_payment_provider
                    ? 'If payment cannot be processed (e.g. contact has no payment method), mark as:'
                    : 'When Upcoming payments are past-due, mark as:'}
                </Label>
                <i
                  className="hint icon-information"
                  data-tip={
                    gym.has_payment_provider
                      ? 'This setting can help automatically reconcile payments for members that are not paying through your chosen payment processor.'
                      : 'This setting can be used to apply an automatic status change for payments that are due, but still have a status of Upcoming.'
                  }
                />
                <Select
                  value={gymMembershipDetail.autofail_override_policy}
                  options={_.compact([
                    { label: 'Failed', value: 'autofail_policy_failed' },
                    { label: 'Skipped', value: 'autofail_policy_skipped' },
                    { label: 'Paid (cash)', value: 'autofail_policy_paid_by_cash' },
                    { label: 'Paid (POS)', value: 'autofail_policy_paid_by_pos' },
                    { label: 'Paid (other)', value: 'autofail_policy_paid_by_other' },
                    !gym.has_payment_provider && {
                      label: 'Process Manually',
                      value: 'autofail_policy_process_manually',
                    },
                  ])}
                  onChange={(autofail_override_policy) =>
                    extendGymMembershipDetail({ autofail_override_policy })
                  }
                />
              </div>
              <div className="form-group">
                <Label>Count new memberships as "reactivations" if:</Label>
                <Select
                  value={gymMembershipDetail.reactivation_policy}
                  options={[
                    {
                      label:
                        'Member previously had a membership, and is now changing from Cancelled to Active',
                      value: 'reactivate_if_cancelled',
                    },
                    {
                      label:
                        "Member's last membership was removed / cancelled yesterday or earlier",
                      value: 'reactivate_if_cancelled_yesterday_or_earlier',
                    },
                    {
                      label:
                        "Member's last membership was removed / cancelled 7 days ago or earlier",
                      value: 'reactivate_if_cancelled_7d_ago_or_earlier',
                    },
                    {
                      label:
                        "Member's last membership was removed / cancelled 30 days ago or earlier",
                      value: 'reactivate_if_cancelled_30d_ago_or_earlier',
                    },
                    {
                      label:
                        "Member's last membership was removed / cancelled 90 days ago or earlier",
                      value: 'reactivate_if_cancelled_90d_ago_or_earlier',
                    },
                    {
                      label:
                        "Member's last membership was removed / cancelled 1 year ago or earlier",
                      value: 'reactivate_if_cancelled_1y_ago_or_earlier',
                    },
                  ]}
                  onChange={(reactivation_policy) =>
                    extendGymMembershipDetail({ reactivation_policy })
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="margin-top-24">
        <Button variant="success" onClick={save}>
          Save
        </Button>
      </div>
    </div>
  );
};

angular.module('gym').value('GymProfileMembershipPayments', GymProfileMembershipPayments);
